import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { ApiError, apiFetch } from '@/lib/api';
import { clearToken, readToken, writeToken } from '@/lib/auth-storage';

export interface AuthUser {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  /** Agency slugs this account may see. Empty means no agency access. */
  agencies: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  /** null while the stored token is still being validated against /me. */
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  mayAccess: (agency: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // A token in localStorage is a claim, not proof: it may be expired or issued
  // by a different environment. Validate it against /me before treating the
  // session as real, otherwise the shell renders and every request 401s.
  useEffect(() => {
    let cancelled = false;
    const token = readToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    apiFetch<AuthUser>('/api/auth/me')
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Only a REJECTED token is grounds for clearing it. Any failure used to
        // count, which meant an aborted request signed the user out -- and the
        // request aborts whenever a navigation lands while the bootstrap is
        // still in flight. The user sees an unprompted bounce to /login with a
        // perfectly good session, and nothing logs an error because nothing
        // errored. Found by an e2e failure that named the symptom exactly.
        //
        // A network failure or an abort leaves the session ALONE: the token may
        // be fine and the next request will find out. Refusing to guess here is
        // the same rule the analytics layer follows -- an absent answer is not
        // evidence of a negative one.
        const rejected = error instanceof ApiError && (error.status === 401 || error.status === 403);
        if (rejected) {
          clearToken();
        }
        setUser(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { access_token } = await apiFetch<{ access_token: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    writeToken(access_token);
    setUser(await apiFetch<AuthUser>('/api/auth/me'));
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  const mayAccess = useCallback(
    (agency: string) =>
      user?.role === 'admin' ||
      user?.role === 'superadmin' ||
      user?.role === 'visitante' ||
      Boolean(user?.agencies.includes(agency)),
    [user],
  );

  const value = useMemo(
    () => ({ user, isLoading, login, logout, mayAccess }),
    [user, isLoading, login, logout, mayAccess],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
